import { useCallback, useEffect, useRef, useState } from 'react';
import ModelPipeline3D from './ModelPipeline3D';
import ModelDetailViewer from './ModelDetailViewer';
import { AUTOMATION_STEPS } from '../data/service';

export default function AutomationExperience() {
  const sectionRef = useRef(null);
  const sceneRef = useRef(null);
  const cardsRef = useRef([]);
  const detailButtonRef = useRef(null);
  const [current, setCurrent] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const step = AUTOMATION_STEPS[current];
  const closeViewer = useCallback(() => setViewerOpen(false), []);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setMounted(true);
      setVisible(true);
      return undefined;
    }
    const preload = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setMounted(true); preload.disconnect(); }
    }, { rootMargin: '240px' });
    const visibility = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.05 });
    preload.observe(sceneRef.current);
    visibility.observe(sceneRef.current);
    return () => { preload.disconnect(); visibility.disconnect(); };
  }, []);

  useEffect(() => {
    if (viewerOpen) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      const bounds = sectionRef.current?.getBoundingClientRect();
      if (!bounds || bounds.bottom < 0 || bounds.top > window.innerHeight) return;
      const line = window.innerWidth <= 800
        ? (sceneRef.current?.getBoundingClientRect().bottom || 350) + 64
        : window.innerHeight * 0.54;
      let next = 0;
      cardsRef.current.forEach((card, index) => {
        if (card && card.getBoundingClientRect().top <= line) next = index;
      });
      setCurrent(next);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [viewerOpen]);

  const selectStep = useCallback((index) => {
    setCurrent(index);
    const card = cardsRef.current[index];
    const offset = window.innerWidth <= 800
      ? (sceneRef.current?.getBoundingClientRect().height || 320) + 100
      : window.innerHeight * 0.38;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (card) window.scrollTo({ top: window.scrollY + card.getBoundingClientRect().top - offset, behavior: reduced ? 'auto' : 'smooth' });
  }, []);

  return (
    <section id="how-it-works" className="mf-experience mf-section" ref={sectionRef} aria-labelledby="experience-title">
      <div className="mf-container">
        <div className="mf-section-heading mf-centered" data-reveal>
          <span className="mf-eyebrow">CONNECTED INTELLIGENCE</span>
          <h2 id="experience-title">데이터가 연결되면,<br />다음 액션이 보입니다.</h2>
          <p>수집에서 예측, 운영까지.<br className="mf-mobile-break" /> 마켓핏의 다섯 가지 연결을 따라가 보세요.</p>
        </div>
        <div className="mf-experience-grid">
          <div className="mf-visual" ref={sceneRef}>
            <div className="mf-visual-head"><span><i /> MARKETFIT ENGINE</span><button ref={detailButtonRef} type="button" className="mf-expand-model" onClick={() => setViewerOpen(true)} aria-label="3D 모델 자세히 보기"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>자세히 보기</button></div>
            <div className="mf-scene" aria-label={`${step.short} 3D 시각화`}>
              {mounted && <ModelPipeline3D model={step.model} stage={step.stage} active={visible && !viewerOpen} cameraScale={1.35} />}
            </div>
            <div className="mf-scene-caption" key={step.id}><span>{step.number} / 05</span><p>{step.scene}</p></div>
            <div className="mf-scene-nav" role="group" aria-label="자동화 흐름 선택">
              {AUTOMATION_STEPS.map((item, index) => (
                <button key={item.id} type="button" aria-pressed={index === current} onClick={() => selectStep(index)}><span>{item.number}</span>{item.short}</button>
              ))}
            </div>
            <div className="mf-scene-note"><span>드래그해서 시점을 바꿔보세요</span><span>서비스 구조 시각화</span></div>
          </div>
          <div className="mf-story">
            {AUTOMATION_STEPS.map((item, index) => (
              <article key={item.id} ref={(element) => { cardsRef.current[index] = element; }} className={`mf-story-step${index === current ? ' is-active' : ''}`} aria-current={index === current ? 'step' : undefined}>
                <div className="mf-story-kicker"><span>{item.number}</span>{item.short}</div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <div className="mf-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
                <div className="mf-story-output"><span aria-hidden="true">↳</span>{item.output}</div>
              </article>
            ))}
          </div>
        </div>
      </div>
      {viewerOpen && <ModelDetailViewer initialModel={step.model} initialStage={step.stage} returnFocusTo={detailButtonRef.current} onClose={closeViewer} />}
    </section>
  );
}
